// CONF-LIVRE (I.181): painel da contagem livre — bipe por código (SKU, GTIN ou interno), lista dos bipados com
// quantidade (contagem cega: sem saldo durante a contagem), e o fechamento com três ações separadas, cada uma
// confirmada: "Ajustar estoque pelo contado", "Zerar saldo dos não bipados" e "Inativar não bipados parados".
// Plano: docs/pacotes/CONF-LIVRE-plano.md (P1–P5, B1, B2).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { StockChecklistItem, StockCheckCountPage } from "@/types/stockChecklist";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, ScanBarcode, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

interface FreeCountState {
  success: boolean;
  error?: string;
  free_count: boolean;
  access_mode?: string;
  status: string;
  name: string;
  deposit_name?: string | null;
  scanned: number;
  completed_at?: string | null;
  listed_at?: string | null;
  zeroed_at?: string | null;
  inactivated_at?: string | null;
  can_manage: boolean;
}

interface ScanResult {
  success: boolean;
  error?: string;
  choose?: boolean;
  options?: { id: string; name: string; sku: string | null }[];
  item_id?: string;
  name?: string;
  sku?: string | null;
  counted?: number;
}

interface UnscannedRow {
  product_id: string;
  name: string;
  sku: string | null;
  stock_at_close: number;
  stock_now: number;
  stock_any_deposit: boolean;
  last_sale_date: string | null;
  idle: boolean;
  block_reason: string | null;
  block_now: string | null;
  moved_during: boolean;
  moved_after: boolean;
  zeroed_at: string | null;
  zeroed_quantity: number | null;
  zero_skip_reason: string | null;
  inactivated_at: string | null;
  active: boolean;
}

const BLOCK_LABEL: Record<string, string> = {
  peca_com_cliente: "Peça com cliente",
  unidade_registrada: "Unidade registrada",
  outra_conferencia: "Em outra conferência aberta",
};

const errMsg = (err: unknown, fallback: string) => (err as { message?: string } | null)?.message || fallback;
const qty = (n: number | null | undefined) => Number(n ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: 3 });
const fmtDate = (d: string | null) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "—");

const isOpen = (status: string | undefined) => status === "draft" || status === "in_progress";

const unscannedSituation = (r: UnscannedRow): string => {
  if (r.inactivated_at) return "Inativado";
  if (r.zeroed_at) return `Zerado (${qty(r.zeroed_quantity)})`;
  if (r.block_now) return BLOCK_LABEL[r.block_now] ?? r.block_now;
  if (r.moved_during) return "Movimentado durante a contagem";
  if (r.moved_after) return "Mexido depois da contagem";
  return "—";
};

const canZero = (r: UnscannedRow) => !r.zeroed_at && !r.block_now && !r.moved_during && !r.moved_after && r.stock_now > 0;
// B2: só produto com saldo zero em todos os depósitos da empresa (o banco confere de novo na hora)
const canInactivate = (r: UnscannedRow) => r.active && !r.inactivated_at && !r.block_now && r.idle && !r.stock_any_deposit;

export function FreeCountPanel({ token }: { token: string }) {
  const navigate = useNavigate();
  const [state, setState] = useState<FreeCountState | null>(null);
  const [items, setItems] = useState<StockChecklistItem[]>([]);
  const [unscanned, setUnscanned] = useState<UnscannedRow[] | null>(null);
  const [code, setCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [choice, setChoice] = useState<{ code: string; options: NonNullable<ScanResult["options"]> } | null>(null);
  const [confirm, setConfirm] = useState<"ajustar" | "zerar" | "inativar" | null>(null);
  const [working, setWorking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadState = useCallback(async () => {
    const { data, error } = await supabase.rpc("get_free_count_state", { p_token: token });
    if (error) { toast.error(errMsg(error, "Não foi possível ler a contagem")); return null; }
    const s = data as unknown as FreeCountState;
    setState(s);
    return s;
  }, [token]);

  const loadItems = useCallback(async () => {
    const all: StockChecklistItem[] = [];
    for (let page = 1; page <= 50; page++) {
      const { data, error } = await supabase.rpc("get_stock_checklist_count_page", {
        p_token: token, p_search: null, p_category: null, p_only_pending: false, p_only_divergent: false,
        p_not_counted_since: null, p_page: page, p_page_size: 100,
      });
      if (error) { toast.error(errMsg(error, "Não foi possível ler os bipados")); break; }
      const p = data as unknown as StockCheckCountPage;
      all.push(...(p.items ?? []));
      if (!p.items || p.items.length < 100) break;
    }
    setItems(all);
  }, [token]);

  const loadUnscanned = useCallback(async (s: FreeCountState | null) => {
    if (!s?.listed_at || !s.can_manage) { setUnscanned(null); return; }
    const { data, error } = await supabase.rpc("get_free_count_unscanned", { p_token: token });
    if (error) { toast.error(errMsg(error, "Não foi possível ler os não bipados")); return; }
    setUnscanned(((data as unknown as { items: UnscannedRow[] }).items) ?? []);
  }, [token]);

  const reloadAll = useCallback(async () => {
    const s = await loadState();
    await loadItems();
    await loadUnscanned(s);
  }, [loadState, loadItems, loadUnscanned]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  const open = isOpen(state?.status);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const scan = async (rawCode: string, productId?: string) => {
    if (!rawCode.trim()) return;
    setScanning(true);
    try {
      const { data, error } = await supabase.rpc("scan_stock_checklist_code", {
        p_token: token, p_code: rawCode, p_product_id: productId ?? null,
      });
      if (error) throw error;
      const r = data as unknown as ScanResult;
      if (!r.success) {
        if (r.choose && r.options) { setChoice({ code: rawCode, options: r.options }); return; }
        toast.error(r.error ?? "Bipe não registrado");
        return;
      }
      toast.success(`${r.sku ? `${r.sku} — ` : ""}${r.name}: ${qty(r.counted)}`);
      setCode("");
      await loadItems();
      setState((prev) => (prev && prev.status === "draft" ? { ...prev, status: "in_progress" } : prev));
    } catch (err) {
      toast.error(errMsg(err, "Bipe não registrado"));
    } finally {
      setScanning(false);
      inputRef.current?.focus();
    }
  };

  const saveQuantity = async (item: StockChecklistItem, value: string) => {
    const n = Number(value.replace(",", "."));
    if (!Number.isFinite(n) || n < 0 || n === Number(item.counted_stock ?? 0)) return;
    const { data, error } = await supabase.rpc("save_stock_check_count", {
      p_token: token, p_item_id: item.id, p_counted_quantity: n,
    });
    const r = data as unknown as { success: boolean; error?: string } | null;
    if (error || !r?.success) { toast.error(errMsg(error, r?.error ?? "Quantidade não salva")); return; }
    await loadItems();
  };

  const runAction = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      const fn = confirm === "ajustar" ? "complete_free_stock_checklist"
        : confirm === "zerar" ? "zero_free_count_unscanned" : "inactivate_free_count_unscanned";
      const { data, error } = await supabase.rpc(fn, { p_token: token });
      if (error) throw error;
      const r = data as unknown as Record<string, number | boolean | string | undefined>;
      if (r.success === false) { toast.error(String(r.error ?? "Ação não concluída")); return; }
      if (confirm === "ajustar") {
        toast.success(`Estoque ajustado pelo contado: ${r.total_adjustments ?? 0} acerto(s); ${r.unscanned ?? 0} não bipado(s) com saldo.`);
      } else if (confirm === "zerar") {
        toast.success(`Saldo zerado em ${r.zeroed ?? 0} produto(s). Pulados: ${r.blocked ?? 0} bloqueado(s), ${r.moved_during ?? 0} movimentado(s) durante a contagem, ${r.moved_after ?? 0} mexido(s) depois.`);
      } else {
        toast.success(`${r.inactivated ?? 0} produto(s) inativado(s).`);
      }
      setConfirm(null);
      await reloadAll();
    } catch (err) {
      toast.error(errMsg(err, "Ação não concluída"));
    } finally {
      setWorking(false);
    }
  };

  const zeroList = useMemo(() => (unscanned ?? []).filter(canZero), [unscanned]);
  const inactivateList = useMemo(() => (unscanned ?? []).filter(canInactivate), [unscanned]);

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const listed = !!state.listed_at;

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/stock-checklist")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Conferências
          </Button>
          <div>
            <h1 className="text-xl font-semibold">{state.name}</h1>
            <p className="text-sm text-muted-foreground">
              Contagem livre · {state.deposit_name ?? "Depósito principal"} · {open ? "aberta" : "fechada"}
            </p>
          </div>
        </div>

        {open && (
          <Card>
            <CardContent className="pt-6">
              <form
                className="flex gap-2"
                onSubmit={(e) => { e.preventDefault(); scan(code); }}
              >
                <div className="relative flex-1">
                  <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={inputRef}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Bipe ou digite o SKU / código de barras e tecle Enter"
                    className="pl-9"
                    disabled={scanning}
                    autoComplete="off"
                  />
                </div>
                <Button type="submit" disabled={scanning || !code.trim()}>
                  {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Bipados ({items.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum produto bipado ainda.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="py-2 pr-2">SKU</th>
                      <th className="py-2 pr-2">Produto</th>
                      <th className="py-2 pr-2 text-right">{listed && state.can_manage ? "Contado" : "Quantidade"}</th>
                      {listed && state.can_manage && (
                        <>
                          <th className="py-2 pr-2 text-right">Sistema</th>
                          <th className="py-2 text-right">Diferença</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className="border-b last:border-0">
                        <td className="py-2 pr-2 font-mono text-xs">{it.sku ?? "—"}</td>
                        <td className="py-2 pr-2">{it.name}</td>
                        <td className="py-2 pr-2 text-right">
                          {open ? (
                            <Input
                              key={`${it.id}-${it.counted_stock}`}
                              defaultValue={String(it.counted_stock ?? 0)}
                              className="h-8 w-20 ml-auto text-right"
                              inputMode="decimal"
                              onBlur={(e) => saveQuantity(it, e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                            />
                          ) : qty(it.counted_stock)}
                        </td>
                        {listed && state.can_manage && (
                          <>
                            <td className="py-2 pr-2 text-right">{qty(it.system_stock_at_count)}</td>
                            <td className="py-2 text-right">{qty(it.difference)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {open && (
          <div className="flex justify-end">
            <Button onClick={() => setConfirm("ajustar")} disabled={items.length === 0}>
              Ajustar estoque pelo contado
            </Button>
          </div>
        )}

        {!open && listed && !state.can_manage && (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">
              Contagem ajustada. A lista dos não bipados é para quem pode cadastrar e inativar produtos.
            </CardContent>
          </Card>
        )}

        {!open && !listed && (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">
              Esta contagem foi fechada sem a lista dos não bipados.
            </CardContent>
          </Card>
        )}

        {listed && state.can_manage && unscanned && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Não bipados com saldo ({unscanned.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  variant="destructive"
                  onClick={() => setConfirm("zerar")}
                  disabled={!!state.zeroed_at || zeroList.length === 0}
                >
                  Zerar saldo dos não bipados ({state.zeroed_at ? "feito" : zeroList.length})
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setConfirm("inativar")}
                  disabled={!state.zeroed_at || !!state.inactivated_at || inactivateList.length === 0}
                >
                  Inativar não bipados parados ({state.inactivated_at ? "feito" : inactivateList.length})
                </Button>
              </div>
              {!state.zeroed_at && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Use primeiro "Zerar saldo dos não bipados": só produto com saldo zero é inativado.
                </p>
              )}
              {unscanned.length === 0 ? (
                <p className="text-sm text-muted-foreground">Todos os produtos com saldo foram bipados.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b">
                        <th className="py-2 pr-2">SKU</th>
                        <th className="py-2 pr-2">Produto</th>
                        <th className="py-2 pr-2 text-right">Saldo no fechamento</th>
                        <th className="py-2 pr-2 text-right">Saldo agora</th>
                        <th className="py-2 pr-2">Última venda</th>
                        <th className="py-2 pr-2">Parado (180 dias)</th>
                        <th className="py-2">Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unscanned.map((r) => (
                        <tr key={r.product_id} className="border-b last:border-0">
                          <td className="py-2 pr-2 font-mono text-xs">{r.sku ?? "—"}</td>
                          <td className="py-2 pr-2">{r.name}{!r.active && <Badge variant="outline" className="ml-2">inativo</Badge>}</td>
                          <td className="py-2 pr-2 text-right">{qty(r.stock_at_close)}</td>
                          <td className="py-2 pr-2 text-right">{qty(r.stock_now)}</td>
                          <td className="py-2 pr-2">{fmtDate(r.last_sale_date)}</td>
                          <td className="py-2 pr-2">{r.idle ? "Sim" : "Não"}</td>
                          <td className="py-2 text-xs">{unscannedSituation(r)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <AlertDialog open={!!choice} onOpenChange={(o) => { if (!o) setChoice(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Código em mais de um produto</AlertDialogTitle>
            <AlertDialogDescription>Escolha qual produto foi bipado com "{choice?.code}".</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {choice?.options.map((o) => (
              <Button
                key={o.id}
                variant="outline"
                className="w-full justify-start h-auto py-2 text-left whitespace-normal"
                onClick={() => { const c = choice.code; setChoice(null); scan(c, o.id); }}
              >
                <span className="font-mono text-xs mr-2">{o.sku ?? "—"}</span>{o.name}
              </Button>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o && !working) setConfirm(null); }}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "ajustar" ? "Ajustar estoque pelo contado"
                : confirm === "zerar" ? "Zerar saldo dos não bipados" : "Inativar não bipados parados"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "ajustar" && `O estoque de ${items.length} produto(s) bipado(s) passa ao contado e a contagem fecha. Os não bipados não mudam nesta ação.`}
              {confirm === "zerar" && `O saldo destes ${zeroList.length} produto(s) vai a zero no depósito, por ajuste de estoque. Ficam de fora os bloqueados e os movimentados durante ou depois da contagem.`}
              {confirm === "inativar" && `Estes ${inactivateList.length} produto(s), sem venda e sem compra há mais de 180 dias e com saldo zero, ficam inativos.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {(confirm === "zerar" || confirm === "inativar") && (
            <div className="max-h-72 overflow-y-auto text-sm border rounded p-2">
              {(confirm === "zerar" ? zeroList : inactivateList).map((r) => (
                <div key={r.product_id} className="flex justify-between py-1 border-b last:border-0">
                  <span><span className="font-mono text-xs mr-2">{r.sku ?? "—"}</span>{r.name}</span>
                  {confirm === "zerar" && <span className="text-muted-foreground">{qty(r.stock_now)}</span>}
                </div>
              ))}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); runAction(); }} disabled={working}>
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
